<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { storeToRefs } from "pinia";
import { Minimize2, PlayIcon, X } from "@lucide/vue";

import LoadingSpinner from "@/components/Reusables/Loading/LoadingSpinner.vue";

import { useTutorialPlayerStore } from "./stores/tutorialPlayer.store";
import getTrupeerEmbedLink from "./utils/getTrupeerEmbedLink";

// The tutorial player, mounted once in the app shell so a video survives navigation.
//
// Two constraints shape everything below. The player is Trupeer's, inside a cross-origin iframe, so
// none of its controls can be styled or driven from here — picture-in-picture is started from their
// own control. And that floating video stays bound to the `<video>` element inside this iframe, so
// the iframe must keep the same DOM position and stay rendered for its whole life: it is never
// re-parented, never behind a `v-if`, and hidden with opacity rather than `display: none`.
const tutorialPlayer = useTutorialPlayerStore();
const { activeTutorial, isHidden } = storeToRefs(tutorialPlayer);
const { closeTutorial, hidePlayer, showPlayer } = tutorialPlayer;

const embedUrl = computed(() => (activeTutorial.value ? getTrupeerEmbedLink(activeTutorial.value.trupeerId) : ""));

// The embed paints nothing until it has loaded, which would leave the frame as a black rectangle.
const isPlayerLoaded = ref(false);
watch(activeTutorial, () => (isPlayerLoaded.value = false));
</script>

<template>
  <aside
    class="z-overlay pointer-events-none fixed inset-0"
    aria-label="Tutorial player">
    <Transition
      name="fade-in"
      leave-active-class="fade-in-leave-active">
      <div
        v-if="activeTutorial"
        key="backdrop"
        class="z-overlay duration-normal ease-standard bg-scrim/50 fixed inset-0 transition-opacity"
        :class="isHidden ? 'pointer-events-none opacity-0' : 'pointer-events-auto opacity-100'"
        @click="closeTutorial" />
    </Transition>
    <Transition
      name="fade-bottom"
      leave-active-class="fade-in-leave-active">
      <div
        v-if="activeTutorial"
        key="player"
        class="rounded-8 shadow-modal z-overlay duration-normal ease-standard fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 overflow-hidden transition-opacity"
        :class="isHidden ? 'pointer-events-none opacity-0' : 'pointer-events-auto opacity-100'"
        :style="{ width: 'min(880px, calc(100vw - 4rem), calc((100dvh - 10rem) * 16 / 9))' }">
        <div class="px-px-12 py-px-8 bg-slate-0/60 flex items-center gap-2 backdrop-blur-lg">
          <h2 class="t-sh4 text-type-default min-w-0 flex-1 truncate">{{ activeTutorial.title }}</h2>
          <button
            type="button"
            class="focus-ring interactive-active duration-fast ease-standard t-caption text-type-default rounded-8 px-px-8 flex h-8 shrink-0 items-center gap-1.5 transition-colors hover:bg-slate-100"
            aria-label="Hide the player, keeping the video playing"
            @click="hidePlayer">
            <Minimize2
              class="icon-sm"
              :stroke-width="1.5" />
            Minimize
          </button>
          <button
            type="button"
            class="focus-ring interactive-active duration-fast ease-standard text-type-muted rounded-8 flex h-8 w-8 shrink-0 items-center justify-center transition-colors hover:bg-slate-100"
            aria-label="Close the tutorial player"
            @click="closeTutorial">
            <X
              class="icon-sm"
              :stroke-width="1.5" />
          </button>
        </div>

        <div class="relative aspect-video w-full bg-slate-900">
          <div
            v-if="!isPlayerLoaded"
            class="absolute inset-0 flex items-center justify-center">
            <LoadingSpinner
              size="lg"
              fill="primary"
              :label="`Loading ${activeTutorial.title}`" />
          </div>
          <!-- keyed on the tutorial so switching videos swaps the src by remounting this one iframe
               in place, rather than leaving the previous video's player behind -->
          <iframe
            :key="activeTutorial.id"
            :src="embedUrl"
            :title="activeTutorial.title"
            class="relative h-full w-full"
            frameborder="0"
            allowfullscreen
            allow="autoplay; fullscreen; encrypted-media; clipboard-write; picture-in-picture"
            @load="isPlayerLoaded = true" />
        </div>
      </div>
    </Transition>
    <button
      v-if="activeTutorial && isHidden"
      key="restore"
      type="button"
      class="focus-ring interactive-active duration-fast ease-standard bg-slate-0 shadow-dropdown z-overlay pointer-events-auto fixed right-4 bottom-4 rounded-full p-4 ring-1 ring-slate-300 transition-colors hover:bg-slate-100 hover:ring-slate-300"
      :aria-label="`Show the tutorial player: ${activeTutorial.title}`"
      @click="showPlayer">
      <PlayIcon
        class="icon-lg text-app-blue-600"
        :stroke-width="1.5" />
    </button>
  </aside>
</template>
